import type { SyntheticEvent } from 'react';
import { useCallback, useState } from 'react';
import type { CategoryOptionFilterItem } from 'graphql-ts-types';

interface UseSelectAllProps {
  handleChange: (newSelection: string[]) => void;
  options: CategoryOptionFilterItem[];
}

interface UseSelectAllReturn {
  areAllSelected: boolean;
  onSelectAll: () => void;
}

interface UseFilterOptionsProps {
  options: CategoryOptionFilterItem[];
  filterKey: string;
  onChange: (filterKey: string, selectedOptions: string[]) => void;
}

interface UseFilterOptionsReturn extends UseSelectAllReturn {
  selectedOptions: string[];
  onMultiSelectOptionChange: (event: SyntheticEvent) => void;
  onSingleSelectOptionChange: (event: SyntheticEvent) => void;
}

const useSelectAll = ({ options, handleChange }: UseSelectAllProps): UseSelectAllReturn => {
  const [areAllSelected, setAllSelected] = useState(
    () => !options.some(option => !option.selected)
  );

  const onSelectAll = useCallback(() => {
    setAllSelected(!areAllSelected);
    if (areAllSelected) {
      handleChange([]);
    } else {
      handleChange(options.map(option => option.key));
    }
  }, [areAllSelected, handleChange, options]);

  return { areAllSelected, onSelectAll };
};

const useFilterOptions = ({
  filterKey,
  options,
  onChange,
}: UseFilterOptionsProps): UseFilterOptionsReturn => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(() =>
    options.filter(option => option.selected).map(option => option.key)
  );

  const handleChange = useCallback(
    newSelection => {
      setSelectedOptions(newSelection);
      onChange(filterKey, newSelection);
    },
    [setSelectedOptions, onChange, filterKey]
  );

  const onMultiSelectOptionChange = useCallback(
    event => {
      const changedOptionKey = event.target.value;
      if (selectedOptions.includes(changedOptionKey)) {
        handleChange(selectedOptions.filter(optionKey => optionKey !== changedOptionKey));
      } else {
        handleChange([...selectedOptions, changedOptionKey]);
      }
    },
    [selectedOptions, handleChange]
  );

  const onSingleSelectOptionChange = useCallback(
    event => handleChange([event.target.value]),
    [handleChange]
  );

  const { areAllSelected, onSelectAll } = useSelectAll({
    options,
    handleChange,
  });

  return {
    selectedOptions,
    onMultiSelectOptionChange,
    onSingleSelectOptionChange,
    areAllSelected,
    onSelectAll,
  };
};

export default useFilterOptions;
